import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, Outlet } from "react-router-dom";
import {
    ArrowUpRight,
    Checks,
    ClockCounterClockwise,
    Copy,
    Funnel,
    GridFour,
    Star,
    Table,
    UserCircleGear,
    UserPlus,
    X,
    Trash,
    MagnifyingGlass
} from "@phosphor-icons/react";
import axiosInstance from "../../api/axiosInstance";
import PageLoader from "../loader/PageLoader";
import { useQuery } from "@tanstack/react-query";
import PastBookings from "./PastBookings";
import ConfirmPrice from "./ConfirmPrice";
import WebCheckin from "./WebCheckin";
import EarlyCheckin from "./EarlyCheckin";
import LateCheckout from "./LateCheckout";
import Dining from "./Dining";
import HouseKeeping from "./HouseKeeping";
import WakeUp from "./WakeUp";
import ConferenceRoom from "./ConferenceRoom";
import ExtendStay from "./ExtendStay";
import Custom from "./Custom";
import RestaurantTable from "./RestaurantTable";
import Wifi from "./Wifi";
import Checkout from "./Checkout";
import RoomChange from "./RoomChange";
import Spa from "./Spa";
import ManageGuestsModal from './ManageGuestsModal/ManageGuestsModal';

import dayjs from "dayjs";
import utc from 'dayjs/plugin/utc';
import DeleteBookingModal from "../DeleteBookingModal/DeleteBookingModal";
import CompleteBookingModal from "../CompleteBookingModal/CompleteBookingModal";
import NotificationProvider from "../NotificationProvider";
import { formatHotel } from "../../api/hotelTime";
import RoomRequestsPanel, {
    activeComponentForType,
    serviceMap,
} from "./RoomRequestsPanel";


// Extend Day.js with the utc plugin
dayjs.extend(utc);

const Bookings = () => {
    const [openFilter, setOpenFilter] = useState(false);
    const [viewChanged, setViewChanged] = useState("grid");
    const [filteredData, setFilteredData] = useState([]);

    const [searchQuery, setSearchQuery] = useState("");
    const [isSearching, setIsSearching] = useState(false);
    const [filters, setFilters] = useState({
        bookingNumber: "",
        pendingRequests: false,
        ongoingRequests: false,
        noRooms: "",
        bookingName: "",
        bookingContact: "",
    });
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const hotelId = localStorage.getItem("hotelId");
    const [openPastBook, setOpenPastBook] = useState(false);
    const [openReq, setOpenReq] = useState(false);
    const [activeComponent, setActiveComponent] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(false);
    const navigate = useNavigate();

    // const fetchBookingsAndRequests = async (hotelId) => {
    //     const [bookingsResponse, requestsResponse] = await Promise.all([
    //         axiosInstance.get(
    //             `/api/v1/hotels/${hotelId}/bookings/active-bookings`,
    //         ),
    //         axiosInstance.get(`/api/v1/hotels/${hotelId}/active-requests`),
    //     ]);

    //     console.log("Booking Response : ",bookingsResponse);
    //     return {
    //         activeBookings: bookingsResponse.data?.activeBookings || [],
    //         activeRequests: requestsResponse.data.data,
    //     };
    // };

    const fetchBookingsAndRequests = async (hotelId) => {
        let bookingsData = [];
        let requestsData = [];
    
        // Fetch bookings data
        try {
            const bookingsResponse = await axiosInstance.get(
                `/api/v1/hotels/${hotelId}/bookings/active-bookings`
            );
            bookingsData = bookingsResponse?.data?.activeBookings || [];
        } catch (error) {
            console.error("Error fetching bookings:", error);
            // Handle error, but still return empty or default value
            bookingsData = [];
        }
    
        // Fetch requests data
        try {
            const requestsResponse = await axiosInstance.get(
                `/api/v1/hotels/${hotelId}/active-requests`
            );
            requestsData = requestsResponse?.data?.data || [];
        } catch (error) {
            console.error("Error fetching requests:", error);
            // Handle error, but still return empty or default value
            requestsData = [];
        }
    
        // Return both bookings and requests data
        return {
            activeBookings: bookingsData,
            activeRequests: requestsData,
        };
    };

    const { data, error, isLoading, refetch, isFetching } = useQuery({
        queryKey: ["bookingsAndRequests", hotelId], // Unique query key
        queryFn: () => fetchBookingsAndRequests(hotelId), // Query function with both requests
        refetchOnWindowFocus: true, // Optional: refetch data when window gains focus
        staleTime: 5000, // Optional: consider data fresh for 5 seconds
        refetchInterval: 5000,
    });

    // useEffect(() => {
    //     if (data) {
    //         // Apply initial filtering or transformation to data
    //         setFilteredData(data.activeBookings); // You can modify this to apply a default filter if needed
    //     }
    //     else{
    //         setFilteredData([]);
    //     }
    // }, [data]);

    useEffect(() => {
        if (!data || !data.activeBookings) {
            setFilteredData([]);
            return;
        }

        const query = searchQuery.toLowerCase().trim();

        if (query === "") {
            // Show all data if the search is empty
            setFilteredData(data.activeBookings);
        } else {
            const filtered = data.activeBookings.filter((booking) =>[booking.booking_name, booking.booking_contact, booking.ota_id]
                    .some((field) =>
                        String(field).toLowerCase().includes(query)
                    )
            );

            setFilteredData(filtered);
        }
    }, [searchQuery, data]);

    // Drop the open detail panel when the query changes: its actions belong to a
    // booking that may no longer be in the visible list. Keyed on searchQuery only,
    // so the 5s refetch does not clear a selection the user is working in.
    useEffect(() => {
        setSelectedBooking(null);
    }, [searchQuery]);







    const handleItemClick = (booking) => {
        setSelectedBooking(booking);
    };
    const trimId = (id) => {
        if (id.length > 2) return id.slice(0, 2) + "...";
        else return id || "";
    };

    const applyFilter = () => {
        let filtered = data?.activeBookings || [];

        // For All Three Status
        if (
            filters.bookingNumber &&
            filters.bookingName &&
            filters.bookingContact &&
            filters.noRooms &&
            filters.pendingRequests &&
            filters.ongoingRequests
        ) {
            const contact = filters.bookingContact || "0";
            const final = parseInt(contact, 10);
            filtered = filtered.filter((d) => {
                const noRoomsAssigned =
                    d.rooms_booked && d.rooms_booked.includes("NA");

                if (
                    d.booking_contact === final &&
                    filters.bookingNumber === d.ota_id &&
                    filters.bookingName === d.booking_name &&
                    (noRoomsAssigned ||
                        d.pending_requests > 0 ||
                        d.ongoing_requests > 0)
                ) {
                    return true;
                } else {
                    return false;
                }
            });
            // console.log("All Status");
            setFilteredData(filtered);
            setOpenFilter(false);
            return;
        }

        // For 2 Status
        if (
            filters.bookingNumber &&
            filters.bookingName &&
            filters.bookingContact &&
            filters.noRooms &&
            filters.pendingRequests
        ) {
            const contact = filters.bookingContact || "0";
            const final = parseInt(contact, 10);
            filtered = filtered.filter((d) => {
                const noRoomsAssigned =
                    d.rooms_booked && d.rooms_booked.includes("NA");

                if (
                    d.booking_contact === final &&
                    filters.bookingNumber === d.ota_id &&
                    filters.bookingName === d.booking_name &&
                    (noRoomsAssigned || d.pending_requests > 0)
                ) {
                    return true;
                } else {
                    return false;
                }
            });
            // console.log("All Status");
            setFilteredData(filtered);
            setOpenFilter(false);
            return;
        }

        if (
            filters.bookingNumber &&
            filters.bookingName &&
            filters.bookingContact &&
            filters.noRooms &&
            filters.ongoingRequests
        ) {
            const contact = filters.bookingContact || "0";
            const final = parseInt(contact, 10);
            filtered = filtered.filter((d) => {
                const noRoomsAssigned =
                    d.rooms_booked && d.rooms_booked.includes("NA");

                if (
                    d.booking_contact === final &&
                    filters.bookingNumber === d.ota_id &&
                    filters.bookingName === d.booking_name &&
                    (noRoomsAssigned || d.ongoing_requests > 0)
                ) {
                    return true;
                } else {
                    return false;
                }
            });
            // console.log("All Status");
            setFilteredData(filtered);
            setOpenFilter(false);
            return;
        }

        if (
            filters.bookingNumber &&
            filters.bookingName &&
            filters.bookingContact &&
            filters.pendingRequests &&
            filters.ongoingRequests
        ) {
            const contact = filters.bookingContact || "0";
            const final = parseInt(contact, 10);
            filtered = filtered.filter((d) => {
                if (
                    d.booking_contact === final &&
                    filters.bookingNumber === d.ota_id &&
                    filters.bookingName === d.booking_name &&
                    (d.pending_requests > 0 || d.ongoing_requests > 0)
                ) {
                    return true;
                } else {
                    return false;
                }
            });
            // console.log("All Status");
            setFilteredData(filtered);
            setOpenFilter(false);
            return;
        }

        // For 1 Status
        if (
            filters.bookingNumber &&
            filters.bookingName &&
            filters.bookingContact &&
            filters.noRooms
        ) {
            const contact = filters.bookingContact || "0";
            const final = parseInt(contact, 10);
            filtered = filtered.filter((d) => {
                const noRoomsAssigned =
                    d.rooms_booked && d.rooms_booked.includes("NA");

                if (
                    d.booking_contact === final &&
                    filters.bookingNumber === d.ota_id &&
                    filters.bookingName === d.booking_name &&
                    noRoomsAssigned
                ) {
                    return true;
                } else {
                    return false;
                }
            });
            // console.log("All Status");
            setFilteredData(filtered);
            setOpenFilter(false);
            return;
        }

        if (
            filters.bookingNumber &&
            filters.bookingName &&
            filters.bookingContact &&
            filters.pendingRequests
        ) {
            const contact = filters.bookingContact || "0";
            const final = parseInt(contact, 10);
            filtered = filtered.filter((d) => {
                if (
                    d.booking_contact === final &&
                    filters.bookingNumber === d.ota_id &&
                    filters.bookingName === d.booking_name &&
                    d.pending_requests > 0
                ) {
                    return true;
                } else {
                    return false;
                }
            });
            // console.log("All Status");
            setFilteredData(filtered);
            setOpenFilter(false);
            return;
        }

        if (
            filters.bookingNumber &&
            filters.bookingName &&
            filters.bookingContact &&
            filters.ongoingRequests
        ) {
            const contact = filters.bookingContact || "0";
            const final = parseInt(contact, 10);
            filtered = filtered.filter((d) => {
                if (
                    d.booking_contact === final &&
                    filters.bookingNumber === d.ota_id &&
                    filters.bookingName === d.booking_name &&
                    d.ongoing_requests > 0
                ) {
                    return true;
                } else {
                    return false;
                }
            });
            // console.log("All Status");
            setFilteredData(filtered);
            setOpenFilter(false);
            return;
        }

        // For Booking Number,Booking Name,Booking Contact
        if (
            filters.bookingNumber &&
            filters.bookingName &&
            filters.bookingContact
        ) {
            const contact = filters.bookingContact || "0";
            const final = parseInt(contact, 10);
            filtered = filtered.filter((d) => {
                if (
                    d.booking_contact === final &&
                    filters.bookingNumber === d.ota_id &&
                    filters.bookingName === d.booking_name
                ) {
                    return true;
                } else {
                    return false;
                }
            });
            setFilteredData(filtered);
            setOpenFilter(false);
            return;
        }

        // For Booking Number and booking Name
        if (filters.bookingNumber && filters.bookingName) {
            filtered = filtered.filter((d) => {
                if (
                    filters.bookingNumber === d.ota_id &&
                    filters.bookingName === d.booking_name
                ) {
                    return true;
                } else {
                    return false;
                }
            });
            setFilteredData(filtered);
            setOpenFilter(false);
            return;
        }

        // For Booking Number and booking Contact
        if (filters.bookingNumber && filters.bookingContact) {
            filtered = filtered.filter((d) => {
                const contact = filters.bookingContact || "0";
                const final = parseInt(contact, 10);
                if (
                    filters.bookingNumber === d.ota_id &&
                    d.booking_contact === final
                ) {
                    return true;
                } else {
                    return false;
                }
            });
            setFilteredData(filtered);
            setOpenFilter(false);
            return;
        }

        // For Booking Name and Booking Contact
        if (filters.bookingName && filters.bookingContact) {
            const contact = filters.bookingContact || "0";
            const final = parseInt(contact, 10);
            // console.log("Contact : ", final);
            filtered = filtered.filter((d) => {
                if (
                    d.booking_contact === final &&
                    filters.bookingName === d.booking_name
                ) {
                    return true;
                } else {
                    return false;
                }
            });

            // console.log("Filter Data : ", filtered);
            setFilteredData(filtered);
            setOpenFilter(false);
            return;
        }

        // For OTA ID
        if (filters.bookingNumber) {
            filtered = filtered.filter((d) => {
                // console.log("Booking Number:", filters.bookingNumber);
                // console.log("Booking OTA ID:", d.ota_id);
                return d.ota_id === filters.bookingNumber;
            });
        }

        // For Booking NAme
        if (filters.bookingName) {
            filtered = filtered.filter((d) => {
                // console.log("Booking Name:", filters.bookingName);
                // console.log("Booking Name:", d.booking_name);
                return d.booking_name === filters.bookingName;
            });
        }

        // For Contact Number
        if (filters.bookingContact) {
            filtered = filtered.filter((d) => {
                // console.log("Booking Contact:", filters.bookingContact);
                // console.log("Booking Contact:", d.booking_contact);
                const contact = filters.bookingContact || "0";
                const final = parseInt(contact, 10);
                return d.booking_contact === final;
            });
        }

        // For No Rooms
        if (filters.noRooms) {
            filtered = filtered.filter((d) => {
                const noRoomsAssigned =
                    d.rooms_booked && d.rooms_booked.includes("NA");
                return noRoomsAssigned;
            });
        }

        // Filter by pending requests
        if (filters.pendingRequests) {
            filtered = filtered.filter((d) => d.pending_requests > 0);
        }

        // Filter by ongoing requests
        if (filters.ongoingRequests) {
            filtered = filtered.filter((d) => d.ongoing_requests > 0);
        }
        setFilteredData(filtered);
        // Narrowing the list can strand the open detail panel on a booking that is
        // no longer visible, leaving its actions pointing at something off-screen.
        setSelectedBooking(null);
        setOpenFilter(false);
    };
    const resetFilters = () => {
        setFilters({
            bookingNumber: "",
            ongoingRequests: "",
            pendingRequests: "",
            noRooms: "",
            bookingName: "",
            bookingContact: "",
        });
        setFilteredData(data?.activeBookings || []);
        setSelectedBooking(null);
        setOpenFilter(false);
    };

    // const handleCardClick = (booking, index) => {
    //     setSelectedBooking(booking);
    //     setActiveCardIndex(index);
    // };

    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFilters((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const bookingRequests = useMemo(
        () =>
            selectedBooking
                ? (data?.activeRequests || []).filter(
                      (request) =>
                          request.booking_id === selectedBooking.booking_id,
                  )
                : [],
        [data, selectedBooking],
    );

    const handleBookingClick = (request) => {
        // Stop event propagation to prevent parent click handlers
        if (serviceMap[request.type] === "Pre-Checkin") {
            // For pre-checkin requests, open manage-guest in new tab
            window.open(`manage-guest/${request.booking_id}`, "_blank");
        } else if (serviceMap[request.type] === "Post-Checkin") {
            // For post-checkin requests, open manage-room in new tab
            window.open(`manage-room/${request.booking_id}`, "_blank");
        }
    };

    const handleManageBooking = (booking, roomId, e) => {
        e.stopPropagation();
        // console.log("??????", roomId);
        // setSelectedBookedRoom({
        //     bookingId: booking.booking_id,
        //     bookedRoomId: roomId, // This should be available in your booking data
        // });
        navigate(`manage-room/${booking.booking_id}`, { state: roomId });
    };

    const handleOpenRequest = (request) => {
        setSelectedRequest({
            type: request.type,
            hotelId: request.hotel_id,
            bookingId: request.booking_id,
            bookedRoomId: request.booked_room_id,
            requestId: request.id,
            activeDriver: request.status,
            roomNumber: request.room_number,
            userId: request.user_id,
        });
        setOpenReq(true);
        setActiveComponent(activeComponentForType(request.type));
    };

    const handleCopy = (text, id) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId(false), 2000); // Reset copied state after 2 seconds
        });
    };

    const renderGridView = () => (
        <div
            className={`grid ${
                selectedBooking
                    ? "w-1/2 lg:grid-cols-2"
                    : "w-full lg:grid-cols-4"
            } grid-cols-1 sm:grid-cols-2  gap-4 h-fit`}
        >
            {filteredData && filteredData.length > 0 ?(
                filteredData.map((d, index) => (
                    <div
                        className={`p-4 bg-white shadow-lg rounded-lg border-2 ${
                            selectedBooking?.booking_id === d.booking_id
                                ? "border-green-500 bg-[#d2f3e6]"
                                : "border-gray-200"
                        } text-sm hover:border-green-500 hover:outline-2 cursor-pointer`}
                        key={d.booking_id}
                        onClick={() => handleItemClick(d)}
                    >
                        {/* For Booking ID */}
                        <div className="flex justify-between items-center mb-2 text-2xl">
                            <h2 className="text-[#BFC4CC] text-lg">Booking#</h2>
                            <div className="flex items-center gap-1">
                                <h2
                                    className="font-semibold text-gray-800 truncate"
                                    title={d.ota_id}
                                >
                                    {trimId(d.ota_id)}
                                </h2>

                                {/* For Copy Purpose of OTA ID */}
                                <button
                                    onClick={() => handleCopy(d?.ota_id, index)}
                                    className="text-gray-500 hover:text-blue-500"
                                    title="Copy to clipboard"
                                >
                                    {copiedId === index ? (
                                        <Checks size={16} />
                                    ) : (
                                        <Copy size={16} />
                                    )}
                                </button>

                                <button>
                                    <ArrowUpRight
                                        weight="bold"
                                        className="text-[#0B22B2]"
                                        // onClick={() => setManageGuest(true)}
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Travelling-with tags (Kids / Pets only) */}
                        {(d.travelling_with?.includes("Kids") ||
                            d.travelling_with?.includes("Pets")) && (
                            <div className="flex flex-wrap gap-1.5 mb-2">
                                {d.travelling_with?.includes("Kids") && (
                                    <span className="flex items-center gap-1 text-xs font-semibold text-[#9A3412] bg-[#FFEDD5] border border-[#FED7AA] rounded-full px-2.5 py-1">
                                        🧒 Travelling with Kids
                                    </span>
                                )}
                                {d.travelling_with?.includes("Pets") && (
                                    <span className="flex items-center gap-1 text-xs font-semibold text-[#166534] bg-[#DCFCE7] border border-[#BBF7D0] rounded-full px-2.5 py-1">
                                        🐾 Travelling with Pets
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Booking Name */}
                        <div className="flex justify-between items-center border-b-[1.5px] py-1.5">
                            <p className="text-gray-500">Booking Name</p>
                            <div className="flex gap-2">
                                <span className="text-sm font-semibold text-[#FF5A5F]">
                                    {d.booking_name &&
                                    d.booking_name.length > 12
                                        ? d.booking_name.substring(0, 12) +
                                          "..."
                                        : d.booking_name || "NA"}
                                </span>
                            </div>
                        </div>

                        {/* Booking Contact */}
                        <div className="flex justify-between items-center border-b-[1.5px] py-1.5">
                            <p className="text-gray-500">Booking Contact</p>
                            <div className="flex gap-2">
                                <span className="text-sm font-semibold text-[#FF5A5F]">
                                    +
                                    {d.booking_contact &&
                                    d.booking_contact.length > 14
                                        ? `${d.booking_contact.substring(
                                              0,
                                              14,
                                          )}...`
                                        : d.booking_contact || "NA"}
                                </span>
                            </div>
                        </div>

                        {/* Booked Rooms */}
                        <div className="flex justify-between items-center border-b-[1.5px] py-1.5">
                            <p className="text-gray-500">Room Booked</p>
                            <div className="flex  gap-2">
                                {d.rooms_booked.slice(0, 5).map((room, idx) => (
                                    <span
                                        className="text-sm font-semibold text-[#FF5A5F]"
                                        key={idx}
                                    >
                                        {room}
                                    </span>
                                ))}

                                {d.rooms_booked.length > 5 && (
                                    <span className="text-sm font-semibold text-[#FF5A5F]">...</span>
                                )}
                            </div>
                        </div>

                        <div className="space-y-1">
                            <div className="flex justify-between items-center border-b-[1.5px] py-1.5">
                                <p className="text-gray-500">
                                    Pending Requests
                                </p>
                                <span className="text-sm text-white bg-[#FF5A5F] rounded-full px-2 py-0.5">
                                    {d.pending_requests}
                                </span>
                            </div>
                            <div className="flex justify-between items-center border-b-[1.5px] py-1.5">
                                <p className="text-gray-500">
                                    Ongoing Requests
                                </p>
                                <span className="text-sm text-white bg-[#0B22B2] rounded-full px-2 py-0.5">
                                    {d.ongoing_requests}
                                </span>
                            </div>

                            <div className="flex justify-between items-center border-b-[1.5px] py-1.5">
                                <p className="text-gray-500">
                                    Booking Code
                                </p>
                                <span className="text-sm text-white bg-[#bfc2d9] rounded-full px-2 py-0.5 flex items-center gap-1">
                                    <span className="text-sm text-white rounded-full px-2 py-0.5">
                                        {d?.booking_code || "NA"}
                                    </span>
                                    <button
                                        onClick={() => handleCopy(d?.booking_code, index)}
                                        className="text-gray-500 hover:text-blue-500"
                                        title="Copy to clipboard"
                                    >
                                        {copiedId === index ? (
                                            <Checks size={16} />
                                        ) : (
                                            <Copy size={16} />
                                        )}
                                    </button>
                                </span>
                            </div>
                        </div>
                    </div>
                ))): (
                    <div className="col-span-full mt-12">
                        <p className="text-center">No active booking found, please use phone number provided at reception</p>
                    </div>

                )
            }
        </div>
    );

    const renderListView = () => (
        <div className={selectedBooking ? "w-1/2" : "w-full"}>
            <table className="w-full table-fixed rounded-2xl overflow-hidden text-sm">
                <thead className="bg-[#EEF0FB]">
                    <tr>
                        <th className="text-[#0B22B2] font-medium p-3 w-[11%]">
                            Room / Booking #
                        </th>
                        <th className="text-[#0B22B2] font-medium p-3 w-[9%]">
                            Rooms
                        </th>
                        <th className="text-[#0B22B2] font-medium p-3 w-[18%]">
                            Booking Contact
                        </th>
                        <th className="text-[#0B22B2] font-medium p-3 w-[21%]">
                            Booking Name
                        </th>
                        <th className="text-[#0B22B2] font-medium p-3 w-[10%]">
                            Pending Requests
                        </th>
                        <th className="text-[#0B22B2] font-medium p-3 w-[10%]">
                            Ongoing Requests
                        </th>
                        <th className="text-[#0B22B2] font-medium p-3 w-[17%]">
                            Booking Code
                        </th>

                        <th className="p-3 w-[4%]"></th>
                    </tr>
                </thead>
                <tbody>
                    {filteredData.map((d, index) => (
                        <tr
                            className={`${
                                selectedBooking?.booking_id === d.booking_id
                                    ? "bg-[#E6FFF5]"
                                    : index % 2 === 0
                                    ? "bg-white"
                                    : "bg-[#EEF0FB]"
                            } cursor-pointer`}
                            key={d.booking_id}
                            onClick={() => handleItemClick(d)}
                        >
                            <td
                                className="text-center p-2 text-xl font-medium text-gray-500 truncate"
                                title={d.booking_id}
                            >
                                {trimId(d.booking_id)}
                            </td>
                            <td className="text-center p-2 text-base font-medium truncate">
                                {d.rooms_booked.length > 3
                                    ? `${d.rooms_booked.slice(0, 3).join(", ")}...`
                                    : d.rooms_booked.join(", ")
                                }
                            </td>
                            <td
                                className="text-center p-2 text-base font-medium truncate"
                                title={d.booking_contact ? `+${d.booking_contact}` : "NA"}
                            >
                                {d.booking_contact
                                    ? `+${d.booking_contact}`
                                    : "NA"}
                            </td>
                            <td
                                className="text-center p-2 text-base font-medium truncate"
                                title={d.booking_name || "NA"}
                            >
                                {d.booking_name && d.booking_name.length > 28
                                    ? d.booking_name.substring(0, 28) + "..."
                                    : d.booking_name || "NA"}
                                {d.travelling_with?.includes("Kids") && (
                                    <span
                                        className="ml-1.5 text-sm"
                                        title="Travelling with Kids"
                                    >
                                        🧒
                                    </span>
                                )}
                                {d.travelling_with?.includes("Pets") && (
                                    <span
                                        className="ml-1.5 text-sm"
                                        title="Travelling with Pets"
                                    >
                                        🐾
                                    </span>
                                )}
                            </td>
                            <td className="text-center p-2">
                                <span className="text-sm text-white bg-[#FF5A5F] rounded-full px-2 py-0.5">
                                    {d.pending_requests}
                                </span>
                            </td>
                            <td className="text-center p-2">
                                <span className="text-sm text-white bg-[#0B22B2] rounded-full px-2 py-0.5">
                                    {d.ongoing_requests}
                                </span>
                            </td>
                            <td className="text-center p-2">
                                <div className="flex items-center justify-center gap-1.5 min-w-0">
                                    <span
                                        className="font-mono font-semibold tracking-wider text-[#262B33] bg-white border-[1.5px] border-[#BFC4CC] rounded-md px-2 py-0.5 truncate"
                                        title={d?.booking_code || "NA"}
                                    >
                                        {d?.booking_code || "NA"}
                                    </span>
                                    {d?.booking_code && (
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleCopy(d.booking_code, index);
                                            }}
                                            className="text-[#8C9199] hover:text-[#0B22B2] shrink-0"
                                            title="Copy booking code"
                                        >
                                            {copiedId === index ? (
                                                <Checks size={16} />
                                            ) : (
                                                <Copy size={16} />
                                            )}
                                        </button>
                                    )}
                                </div>
                            </td>
                            <td>...</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // const handleManageGuest = (bookingId, roomId, e) => {
    //     e.stopPropagation();
    //     // console.log("Booking Details:", bookingId);

    //     // Construct the full URL based on your current location
    //     const url = `${window.location.origin}/dashboard/bookings/manage-guest/${bookingId}/${roomId}`;

    //     // Open the constructed URL in a new tab
    //     window.open(url, "_blank", "noopener,noreferrer");
    // };

    // const handleManageRoom = (bookingId, roomId, e) => {
    //     const url = `${window.location.origin}/dashboard/bookings/manage-room/${bookingId}/${roomId}`;

    //     // Open the constructed URL in a new tab
    //     window.open(url, "_blank", "noopener,noreferrer");
    // };

    const handleCloseDetail = () => {
        setSelectedBooking(null);
    };

    
  

    // State Modal
    const [manageGuestModal,setManageGuestModal] = useState(false);
    const [bookingData,setBookingData] = useState(null);
    const [bookedRoomsData,setBookedRoomsData] = useState(null);
    const [modalLoader,setModalLoader] = useState(false);
    // const [roomsData,setRoomsData] = useState(null);

    // Format Date
    const formatDateForInput = (date) => {
        return formatHotel(date, "YYYY-MM-DDTHH:mm");
    };
    
      
    useEffect(()=>{
        if(bookingData && bookedRoomsData){
            setManageGuestModal(true);
        }

    },[bookingData,bookedRoomsData])

    // Manage Guest Modal
    const manageGuestsModalOpen = async(bookingId)=>{

        try{
            setModalLoader(true);
            // Get Booking By ID
            const response = await axiosInstance.get(`/api/v1/hotels/${hotelId}/bookings/${bookingId}`)
            console.log("Response For Bookings : ",response.data);

            console.log("Check Out Date : ",response?.data?.checkin_date);
            console.log("Check In Date : ",response?.data?.checkout_date);

            // return;

            const checkInDate = formatDateForInput(response?.data?.checkin_date || "");
            const checkOutDate = formatDateForInput(response?.data?.checkout_date || "");


            // Booking Data
            const bookingData = {
                bookingId : bookingId,
                checkInDate :checkInDate,
                checkOutDate : checkOutDate,
                name : response?.data?.name || "",
                contact : response?.data?.contact || "",
                ota_id : response?.data?.ota_id || "",
                rooms : response?.data?.rooms || "",
                email : response.data?.booking_email || "",
                number_of_people : response?.data?.number_of_people || "",
                booking_code : response?.data?.booking_code || ""
            };
            
            setBookingData(bookingData);
            try{

                const response1 = await axiosInstance.get(`/api/v1/hotels/${hotelId}/bookings/${bookingId}/booked-rooms`);
                const rooms = response1?.data?.data || [];
                // console.log("Booked Rooms Data to be Sent : ",response1.data.data);
                const sortedRooms = rooms.sort((a, b) => a.id.localeCompare(b.id));

                setBookedRoomsData(sortedRooms);
    
            }catch(error){
                console.log(error);
            }

            // try{

            //     const roomsResponse = await axiosInstance.get(`/api/v1/hotels/${hotelId}/rooms`);
            //     const roomResponseData = roomsResponse?.data?.data;
            //     setRoomsData(roomResponseData)

            // }catch(error){
            //     console.log(error);
            // }
  
 
 

        }catch(error){
            console.log(error);
        }
        finally{
            setModalLoader(false);
        }
    }


  

    // Delete Booking
    const [deleteBookingModal,setDeleteBookingModal] = useState(false);
    const [deleteBookingId,setDeleteBookingId] = useState("");
    const handleDeleteBooking = (bookingId)=>{
        try{
            setDeleteBookingId(bookingId);
           
        }catch(error){
            console.log(error)
        }
    }

    useEffect(()=>{
        const setUpDeleteId = ()=>{
            if(deleteBookingId){
                setDeleteBookingModal(true);
            }
        }
        setUpDeleteId();

    },[deleteBookingId,setDeleteBookingId])




    // Complete Booking Modal
    const [completeBoookingModal,setCompleteBookingModal] = useState(false);
    const [completeBookingId,setCompleteBookingId] = useState("");

    const handleCompleteBooking = (bookingId)=>{
        try{
            // console.log("Clicked Booking :",bookingId)
            setCompleteBookingId(bookingId);
        }catch(error){
            console.log(error);
        }
    }

    useEffect(()=>{
        const compelteIDBooking = ()=>{
            if(completeBookingId!=""){
                // console.log("Here")
                
                setCompleteBookingModal(true);
            }
        }
        compelteIDBooking();
    },[completeBookingId,setCompleteBookingId])



    // console.log("Filter Data : ",filteredData)


    return (
        <div>
            {!isFetching && isLoading && <PageLoader />}
            <NotificationProvider />

            {modalLoader && <PageLoader/>}

            {openPastBook ? (
                <PastBookings close={setOpenPastBook} />
            ) : (
                <div className="w-full">
                    {/* Sub -Header */}
                    <div className="flex justify-between items-center mb-3">
                        <div className="flex items-center gap-2">
                            <h2 className="text-xl font-semibold tracking-[4px]">
                                BOOKINGS
                            </h2>
                            <div className="flex items-center border-[1.5px] w-fit rounded-lg overflow-hidden">
                                <button
                                    className={`p-2 border-r-[1.5px] ${
                                        viewChanged === "table"
                                            ? "bg-[#ff7378] text-white"
                                            : "text-[#ff7378]"
                                    }`}
                                    onClick={() => setViewChanged("table")}
                                >
                                    <Table weight="fill" />
                                </button>
                                <button
                                    className={`p-2 border-r-[1.5px] ${
                                        viewChanged === "grid"
                                            ? "bg-[#ff7378] text-white"
                                            : "text-[#ff7378]"
                                    }`}
                                    onClick={() => setViewChanged("grid")}
                                >
                                    <GridFour weight="fill" />
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Search Functionality */}
                            <div className="flex items-center gap-2">
                                {isSearching ? (
                                    <>
                                    <input
                                        type="text"
                                        name="search"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Search booking by contact, name, number"
                                        autoFocus
                                        autoComplete="off"
                                        className="
                                        bg-gray-200 rounded-lg px-4 py-2
                                        focus:outline-none transition-all duration-300 ease-in-out
                                        w-[350px]
                                        text-sm
                                        "
                                    />
                                    <button
                                        className="bg-gray-200 px-3 py-2 rounded-lg flex items-center justify-center"
                                        onClick={() => {setIsSearching(false);setSearchQuery("");}}
                                        aria-label="Close search"
                                    >
                                        <X size={20} />
                                    </button>
                                    </>
                                ) : (
                                    <button
                                    className="bg-gray-200 px-4 py-2 rounded-lg flex items-center gap-2"
                                    onClick={() => setIsSearching(true)}
                                    >
                                    <MagnifyingGlass size={20} /> Search
                                    </button>
                                )}
                            </div>

                            <button
                                className="bg-gray-200 px-4 py-2 rounded-lg flex items-center gap-2"
                                onClick={() => {
                                    setOpenPastBook(true);
                                }}
                            >
                                <ClockCounterClockwise size={18} />
                                Past Bookings
                            </button>
                            {openReq && (
                                <>
                                    {activeComponent === "ConfirmPrice" && (
                                        <ConfirmPrice
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                    {activeComponent === "WebCheckin" && (
                                        <WebCheckin
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                    {activeComponent ===
                                        "PreCheckin Early Checkin Request" && (
                                        <EarlyCheckin
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                    {activeComponent ===
                                        "PostCheckin Late Checkout Request" && (
                                        <LateCheckout
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                    {activeComponent === "Dining" && (
                                        <Dining
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                    {activeComponent === "HouseKeeping" && (
                                        <HouseKeeping
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                    {activeComponent === "WakeUp" && (
                                        <WakeUp
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}{" "}
                                    {activeComponent === "Conference" && (
                                        <ConferenceRoom
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                    {activeComponent === "extendStay" && (
                                        <ExtendStay
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                    {activeComponent === "Custom" && (
                                        <Custom
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                    {activeComponent === "Restaurant Table" && (
                                        <RestaurantTable
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                    {activeComponent === "Get Wifi" && (
                                        <Wifi
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}{" "}
                                    {activeComponent === "Checkout" && (
                                        <Checkout
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}{" "}
                                    {activeComponent ===
                                        "PostCheckin Room Change Request" && (
                                        <RoomChange
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                    {activeComponent === "Spa" && (
                                        <Spa
                                            func={setOpenReq}
                                            selectedRequest={selectedRequest}
                                        />
                                    )}
                                </>
                            )}
                            <div className="relative inline-block">
                                <button
                                    className="bg-gray-200 px-4 py-2 rounded-lg flex items-center gap-2"
                                    onClick={() => setOpenFilter(!openFilter)}
                                >
                                    <Funnel size={18} />
                                    Filter
                                </button>

                                {openFilter && (
                                    <div className="absolute top-full right-0 mt-2 border-[1.5px] rounded-md bg-white text-sm w-96 overflow-hidden z-50">
                                        <div className="flex items-center justify-between bg-[#F2F7FF] px-3 py-2">
                                            <h2 className="font-semibold">
                                                Filters
                                            </h2>
                                            <button
                                                onClick={() =>
                                                    setOpenFilter(false)
                                                }
                                                className="bg-[#F2F7FF] p-1.5 border-[1.5px] rounded-md"
                                            >
                                                <X weight="bold" size={15} />
                                            </button>
                                        </div>
                                        <div className="p-3 flex flex-col gap-3">

 

                                            {/* Booking Status */}
                                            <div>
                                                <label
                                                    htmlFor=""
                                                    className="font-medium"
                                                >
                                                    Any Booking With
                                                </label>
                                                <div className="flex px-1 mt-1 flex-wrap">
                                                    <div className="flex items-center gap-1.5 w-1/2">
                                                        <input
                                                            type="checkbox"
                                                            value="pendingRequests"
                                                            id="pendingRequests"
                                                            name="pendingRequests"
                                                            checked={
                                                                filters.pendingRequests
                                                            }
                                                            onChange={
                                                                handleFilterChange
                                                            }
                                                        />
                                                        <label htmlFor="pendingRequests">
                                                            Pending Requests
                                                        </label>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 w-1/2">
                                                        <input
                                                            type="checkbox"
                                                            value="ongoingRequests"
                                                            name="ongoingRequests"
                                                            id="ongoingRequests"
                                                            checked={
                                                                filters.ongoingRequests
                                                            }
                                                            onChange={
                                                                handleFilterChange
                                                            }
                                                        />
                                                        <label htmlFor="ongoingRequests">
                                                            Ongoing Requests
                                                        </label>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 w-1/2">
                                                        <input
                                                            type="checkbox"
                                                            value="noRooms"
                                                            name="noRooms"
                                                            id="noRooms"
                                                            checked={
                                                                filters.noRooms
                                                            }
                                                            onChange={
                                                                handleFilterChange
                                                            }
                                                        />
                                                        <label htmlFor="noRooms">
                                                            No Rooms Assigned
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Booking Id */}
                                            <div>
                                                <label htmlFor="">
                                                    Booking Number
                                                </label>
                                                <select
                                                    className="border-[1.5px] p-2 rounded-md mt-1 w-full"
                                                    name="bookingNumber"
                                                    value={
                                                        filters.bookingNumber
                                                    }
                                                    onChange={
                                                        handleFilterChange
                                                    }
                                                >
                                                    <option
                                                        value=""
                                                        className="text-gray-500"
                                                    >
                                                        Choose Booking #
                                                    </option>
                                                    {data &&
                                                        Array.isArray(
                                                            data?.activeBookings,
                                                        ) &&
                                                        data.activeBookings.map(
                                                            (d, index) => (
                                                                <option
                                                                    key={index}
                                                                    value={
                                                                        d.ota_id
                                                                    }
                                                                >
                                                                    {d.ota_id}
                                                                </option>
                                                            ),
                                                        )}
                                                </select>
                                            </div>

                                            {/* Booking Name */}
                                            <div>
                                                <label htmlFor="">
                                                    Choose Booking Name
                                                </label>
                                                <select
                                                    className="border-[1.5px] p-2 rounded-md mt-1 w-full"
                                                    name="bookingName"
                                                    value={
                                                        filters.bookingName ||
                                                        ""
                                                    }
                                                    onChange={
                                                        handleFilterChange
                                                    }
                                                >
                                                    <option
                                                        value=""
                                                        className="text-gray-500"
                                                    >
                                                        Choose Booking Name
                                                    </option>
                                                    {data &&
                                                        Array.isArray(
                                                            data?.activeBookings,
                                                        ) &&
                                                        data?.activeBookings
                                                            .length > 0 &&
                                                        data?.activeBookings.map(
                                                            (d, index) => (
                                                                <option
                                                                    key={index}
                                                                    value={
                                                                        d.booking_name
                                                                    }
                                                                >
                                                                    {
                                                                        d.booking_name
                                                                    }
                                                                </option>
                                                            ),
                                                        )}
                                                </select>
                                            </div>

                                            {/* Booking Contact */}
                                            <div>
                                                <label htmlFor="">
                                                    Choose Booking Contact
                                                </label>
                                                <select
                                                    className="border-[1.5px] p-2 rounded-md mt-1 w-full"
                                                    name="bookingContact"
                                                    value={
                                                        filters.bookingContact
                                                    }
                                                    onChange={
                                                        handleFilterChange
                                                    }
                                                >
                                                    <option
                                                        value=""
                                                        className="text-gray-500"
                                                    >
                                                        Choose Booking Contact
                                                    </option>
                                                    {data &&
                                                    Array.isArray(data.activeBookings) &&
                                                    [...new Set(data.activeBookings.map(d => d.booking_contact))].map(
                                                        (contact, index) => (
                                                        <option key={index} value={contact}>
                                                            {contact}
                                                        </option>
                                                        )
                                                    )}

                                                </select>
                                            </div>


                                        </div>
                                        <div className="bg-[#F2F7FF] px-3 py-2 flex items-center justify-between">
                                            <button
                                                className="text-[#ff7378] border-[1.5px] rounded-md px-2 py-1"
                                                onClick={resetFilters}
                                            >
                                                Reset
                                            </button>
                                            <button
                                                className="bg-[#ff7378] px-3 py-1 rounded-md"
                                                onClick={applyFilter}
                                            >
                                                Apply Filter
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* {manageGuest && (
                        <ManageGuest
                            func={setManageGuest}
                            selectedBooking={selectedBooking}
                        />
                    )} */}
                    {data && (
                        <div className="flex">
                            {viewChanged === "grid"
                                ? renderGridView()
                                : renderListView()}
                            {selectedBooking && (
                                <div className="w-1/2 ml-3 bg-[#96fdd4] rounded-md h-fit  sticky top-20">
             
                                    <div className="flex justify-center mt-3 mb-0">
                                        <div className="flex justify-end items-center gap-3 w-[95%]">
                                            {/* <button className="text-[#595E66] rounded-md border-[1.5px] border-[#8f96a3] px-3 py-1.5 text-sm bg-white">
                                                Change Room
                                            </button> */}


                                            {/* <button
                                                className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#0B22B2] text-white text-sm"
                                                onClick={() => {
                                                    navigate(
                                                        `manage-guest/${selectedBooking.booking_id}/${selectedBooking.booked_room_id}`,
                                                    );
                                                }}
                                            >
                                                <UserCircleGear
                                                    weight="bold"
                                                    size={17}
                                                />
                                                Manage Guests
                                            </button> */}

                                            {/* Manage Guest Button By Kushal Khandhara */}
                                            <button
                                                className="flex items-center gap-2 px-3 py-2 rounded-md bg-[#0B22B2] text-white text-sm"
                                                onClick={()=>manageGuestsModalOpen(selectedBooking.booking_id)}   
                                            >
                                                <UserCircleGear
                                                    weight="bold"
                                                    size={17}

                                                />
                                                Manage Guests
                                            </button>
                                          
                                            <button className="flex items-center gap-2 px-3 py-2 text-white rounded-md bg-[#0a411684] text-sm"
                                                onClick={()=>handleCompleteBooking(selectedBooking.booking_id)}
                                            >
                                                <Checks   /> Complete Booking
                                            </button>

                                            <button className="flex items-center gap-2 px-3 py-2 text-white rounded-md bg-red-400 text-sm"
                                              onClick={()=>handleDeleteBooking(selectedBooking.booking_id)}
                                            >
                                                <Trash/>
                                                Delete Booking
                                            </button>



                                            <button
                                                onClick={handleCloseDetail}
                                                className="hover:bg-[#F2F7FF] p-2 border-[1.5px] rounded-md bg-white"
                                                type="button"
                                            >
                                                <X weight="bold" size={18} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="flex justify-center my-0 mb-3 w-full">
                                        <RoomRequestsPanel
                                            requests={bookingRequests}
                                            onOpenRequest={handleOpenRequest}
                                        />
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                    <Outlet context={{ selectedBooking }} />
                </div>
            )}

            {/* Manage Guests By Kushal */}
            {manageGuestModal && 
                <ManageGuestsModal
                    manageGuestModal = {manageGuestModal}
                    setManageGuestModal = {setManageGuestModal}
                    bookingData = {bookingData}
                    bookedRoomsData = {bookedRoomsData}
                    setBookedRoomsData = {setBookedRoomsData}
                    setBookingData = {setBookingData}
                    // roomsData = {roomsData}
                    // setRoomsData = {setRoomsData}
                />
            }
            
            {/* Delete Booking Modal */}
            {
                deleteBookingModal &&
                <DeleteBookingModal
                    deleteBookingModal = {deleteBookingModal}
                    setDeleteBookingModal = {setDeleteBookingModal}
                    deleteBookingId = {deleteBookingId}
                    setDeleteBookingId = {setDeleteBookingId}
                    selectedBooking = {selectedBooking}
                    setSelectedBooking = {setSelectedBooking}
                
                />
            }


            {/* Complete Booking Modal */}
            {
                completeBoookingModal &&
                <CompleteBookingModal
                    completeBoookingModal = {completeBoookingModal}
                    setCompleteBookingModal = {setCompleteBookingModal}
                    completeBookingId = {completeBookingId}
                    setCompleteBookingId = {setCompleteBookingId}
                    selectedBooking = {selectedBooking}
                    setSelectedBooking = {setSelectedBooking}
                />
            }


        </div>
    );
};

export default Bookings;
